import { ATTR_NAME } from '../../../constants/attr-name'
import { CLASS_NAME } from '../../../constants/class-names'
import {
    ComponentRenderType,
    EventType,
    HtmlAttr,
    KeyType,
    MutationType,
    CarouselType
} from 'constants/enums'
import { Nullable } from 'constants/types'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { TabIndexUtils } from 'utils/tab-index'
import { CSSUtils } from 'utils/css'

export const WINDOW_RESIZE_TIMEOUT = 300
export const SCROLLING_TIMEOUT = 75

const { carousel: carouselClass } = CLASS_NAME
const { carousel: carouselAttr } = ATTR_NAME.main

// Abstracted carousel functionality
export class CarouselComponent extends AbstractObserver {
    public static type = ComponentRenderType.CAROUSEL

    public static defaultType = CarouselType.SLIDE
    /* eslint-disable no-magic-numbers */
    public static buttonViewScrollThreshold = 20
    public static extraListPageThreshold = 100
    public pageIndex = 0
    public lastScrollPosition = 0
    public paginationConfigured = false
    public fullscreenEvent = false

    // Options
    public type: CarouselType
    public slidesToScroll: number
    // Elements & Calculated Values
    public slider: HTMLElement
    public slides: HTMLElement[]
    public totalSlides: number
    public slideItemWidth: number
    // pagination
    public totalPages: number
    public currentPageIndicator: Nullable<HTMLElement>
    public totalPageIndicator: Nullable<HTMLElement>
    public paginationNavigation: Nullable<HTMLElement>
    public paginationNavigationActionItems: HTMLElement[]
    // prev & next
    public prevButton: Nullable<HTMLButtonElement>
    public nextButton: Nullable<HTMLButtonElement>
    /* eslint-enable no-magic-numbers */

    /**
     * Initializes the carousel
     *
     * @param {HTMLElement} el
     */
    constructor(protected sliderContainer: HTMLElement) {
        super(sliderContainer)
        this.activateObserver()
    }

    public render(): void {
        this.type =
            (this.sliderContainer.getAttribute(
                carouselAttr.type
            ) as CarouselType) || CarouselComponent.defaultType

        const slider = this.getElement(
            `[${carouselAttr.slideList}]`,
            this.sliderContainer
        )
        if (!slider) {
            return
        }
        this.slider = slider
        this.lastScrollPosition = this.slider.scrollLeft
        this.getSlidesInfo()
        this.currentPageIndicator = this.getElement(
            `[${carouselAttr.pageNumber}]`,
            this.sliderContainer
        )
        this.totalPageIndicator = this.getElement(
            `[${carouselAttr.pageTotal}]`,
            this.sliderContainer
        )

        this.prevButton = this.getElement<HTMLButtonElement>(
            `[${carouselAttr.prevBtn}]`,
            this.sliderContainer
        )
        this.nextButton = this.getElement<HTMLButtonElement>(
            `[${carouselAttr.nextBtn}]`,
            this.sliderContainer
        )
        this.paginationNavigation = this.sliderContainer.querySelector<
            HTMLElement
        >(`[${carouselAttr.paginationNav}]`)
        if (this.paginationNavigation) {
            this.paginationNavigationActionItems = this.getElements(
                `.${carouselClass.paginationListItemButton}`,
                this.paginationNavigation
            )
        }

        // * initialize calcs for first values
        this.totalPages = this.calcTotalPages
        // check if scrolled past page 1
        this.pageIndex = this.currentPageIndex
        this.updatePageTotalUI()
        this.updatePageNumberUI()
        this.configPaginationNavigationItems()

        // * Attach event handlers to custom buttons
        if (this.prevButton) {
            this.prevButton.addEventListener(EventType.CLICK, () =>
                this.goPrev()
            )
            this.prevButton.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => {
                    if (e.key == KeyType.ENTER) {
                        e.preventDefault()
                        this.goPrev()
                    }
                }
            )
            // slider starting scrolled later than the first page make prev btn visible
            if (this.pageIndex > 1) {
                TabIndexUtils.removeTabIndex(this.prevButton)
                CSSUtils.addClass(this.prevButton, carouselClass.btnVisible)
            }
        }
        if (this.nextButton) {
            this.nextButton.addEventListener(EventType.CLICK, () =>
                this.goNext()
            )
            this.nextButton.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => {
                    if (e.key == KeyType.ENTER) {
                        e.preventDefault()
                        this.goNext()
                    }
                }
            )
            // slider has more than 1 page make next btn visible
            if (this.totalPages > 1) {
                TabIndexUtils.removeTabIndex(this.nextButton)
                CSSUtils.addClass(this.nextButton, carouselClass.btnVisible)
            }
        }

        // * respond to change events
        // resize will change the responsive slide container width and therefore pages total
        let isResizing: NodeJS.Timeout
        const resizeObserver = new ResizeObserver(
            (entries: ResizeObserverEntry[]) => {
                for (const entry of entries) {
                    if (entry.contentBoxSize) {
                        window.clearTimeout(isResizing)

                        // Set a timeout to run after resizing ends
                        isResizing = setTimeout(() => {
                            // responsive: when resizing has stopped determine if view needs updating
                            this.pageIndex = this.currentPageIndex
                            this.updatePageNumberUI()
                            if (this.type == CarouselType.LIST) {
                                this.totalPages = this.calcTotalPages
                                this.updatePageTotalUI()
                                this.configPaginationNavigationItems()
                            }
                            this.handleButtonVisibility()
                        }, WINDOW_RESIZE_TIMEOUT)
                    }
                }
            }
        )

        resizeObserver.observe(this.slider)

        const mutationObserver = new MutationObserver(
            (mutationList: MutationRecord[]) => {
                for (const mutation of mutationList) {
                    // the slides within the slider have changed, recalc
                    if (mutation.type === MutationType.CHILD_LIST) {
                        this.getSlidesInfo()
                        this.pageIndex = this.currentPageIndex
                        this.updatePageNumberUI()
                        if (this.type == CarouselType.LIST) {
                            this.totalPages = this.calcTotalPages
                            this.updatePageTotalUI()
                            this.configPaginationNavigationItems()
                        }
                        this.handleButtonVisibility()
                    }
                }
            }
        )

        mutationObserver.observe(this.slider, { childList: true })

        // watch if resize was actually fullscreen toggle
        document.addEventListener(EventType.FULLSCREEN_CHANGE, () => {
            this.fullscreenEvent = true
        })

        // user can wheel scroll, touch scroll, keyboard or use custom buttons;
        // keep component data points and view up to date
        // also fires during full screen API change
        let isScrolling: NodeJS.Timeout
        this.slider.addEventListener(EventType.SCROLL, () => {
            // Clear our timeout throughout the scroll
            window.clearTimeout(isScrolling)

            // Set a timeout to run after scrolling ends
            isScrolling = setTimeout(() => {
                // responsive: when scrolling has stopped determine if view needs updating
                if (this.fullscreenEvent) {
                    // if fullscreen event resize recenter the current slide
                    this.goToIndex(this.pageIndex)
                    this.fullscreenEvent = false
                }
                this.pageIndex = this.currentPageIndex
                this.updatePageNumberUI()
                this.handleButtonVisibility()
                this.handlePaginationNavigationActiveView()
            }, SCROLLING_TIMEOUT)
        })

        // highjack natural arrow to use component function
        this.slider.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
            switch (e.key) {
                case KeyType.RIGHT:
                case KeyType.RIGHT_IE:
                    e.preventDefault()
                    this.goNext()
                    break
                case KeyType.LEFT:
                case KeyType.LEFT_IE:
                    e.preventDefault()
                    this.goPrev()
                    break
                default:
                    break
            }
        })
    }

    /**
     * get the most up to date slider width for page width calcs
     * (changes on resize)
     * @return {number}
     */
    get pageWidth(): number {
        if (!this.slider) {
            return 0
        }
        return this.slider.clientWidth
    }

    /**
     * calc how many horizontal view "pages" are available to scroll in the element
     * @return {number}
     */
    get calcTotalPages(): number {
        if (!this.slider) {
            return 0
        }

        switch (this.type) {
            case CarouselType.LIST: {
                // if all elements are in view on current screen size there is only 1 page
                if (
                    this.slider.scrollWidth - this.slider.clientWidth <=
                    CarouselComponent.buttonViewScrollThreshold
                ) {
                    return 1
                }
                // do the pages fit within the scroll width available
                const pageRemainder = this.slider.scrollWidth % this.pageWidth
                let pagesPerPageWidth = Math.floor(
                    this.slider.scrollWidth / this.pageWidth
                )
                // if the remainder was large enough add another page
                // to be able to scroll to it
                if (pageRemainder >= CarouselComponent.extraListPageThreshold) {
                    pagesPerPageWidth += 1
                }
                return pagesPerPageWidth
            }
            case CarouselType.SLIDE:
            default:
                return this.totalSlides
        }
    }

    /**
     * calc which page the horizontal scroll is sitting at
     * manual scroll can end up half way between 2 pages
     * @return {number}
     */
    get currentPageIndex(): number {
        if (!this.slider) {
            return 0
        }
        // Element scroll left 0 = first page
        if (this.slider.scrollLeft == 0) {
            return 0
        }
        const pageScroll = parseFloat(
            (this.slider.scrollLeft / this.pageWidth).toFixed(2)
        )

        switch (this.type) {
            case CarouselType.LIST: {
                const listPageThreshold = 0.2

                if (pageScroll % 1 > listPageThreshold) {
                    return Math.ceil(pageScroll)
                } else {
                    return Math.round(pageScroll)
                }
            }
            case CarouselType.SLIDE:
                return Math.round(pageScroll)
        }
    }

    /**
     * calc how much to shift the scroll
     * to an index to shift to certain page location/distance
     * @return {number} shift total
     */
    public indexScrollingDistance(index: number): number {
        if (!this.slider) {
            return 0
        }
        const indexDistance = this.pageWidth * index
        const totalDistance = this.slider.scrollWidth
        const finalDistance =
            totalDistance < indexDistance ? totalDistance : indexDistance
        return finalDistance
    }

    /**
     * when navigating keep track of the current page
     * update last scroll position tracker
     * update pagination in UI
     * @param {number}
     */
    public updatePageNumberUI(): void {
        this.lastScrollPosition = this.slider ? this.slider.scrollLeft : 0
        const currentPage = this.pageIndex + 1
        if (this.currentPageIndicator) {
            this.currentPageIndicator.textContent = currentPage.toString()
        }
    }

    /**
     * determine if a responsive reflow has changed the total pages
     * update pagination in UI
     */
    public updatePageTotalUI(): void {
        if (
            this.totalPageIndicator &&
            parseInt(this.totalPageIndicator.textContent as string) !=
                this.totalPages
        ) {
            this.totalPageIndicator.textContent = this.totalPages.toString()
        }
    }

    public getSlidesInfo(): void {
        this.slides = this.getElements(
            `[${carouselAttr.slideItem}]`,
            this.slider
        )
        this.totalSlides = this.slides.length
        this.slideItemWidth =
            this.slides && this.slides[0] ? this.slides[0].clientWidth : 0
    }

    /**
     * determine if a responsive reflow has changed the total pages
     * update pagination in UI
     */
    public getPaginationNavItemIndex(navBtn: Element): number {
        if (!navBtn) {
            return 0
        }
        return parseInt(
            navBtn.getAttribute(carouselAttr.paginationNavToIndex) || '0'
        )
    }

    /**
     * for each navigation item button attach click event
     * to navigate to given index
     */
    public attachPaginationNavItemEvents(): void {
        if (
            this.paginationNavigationActionItems &&
            this.paginationNavigationActionItems.length > 0
        ) {
            this.paginationNavigationActionItems.forEach((navBtn) => {
                if (navBtn) {
                    const index = this.getPaginationNavItemIndex(navBtn)
                    navBtn.addEventListener(EventType.CLICK, () =>
                        this.goToIndex(index)
                    )
                    navBtn.addEventListener(
                        EventType.KEYDOWN,
                        (e: KeyboardEvent) => {
                            if (e.key == KeyType.ENTER) {
                                e.preventDefault()
                                this.goToIndex(index)
                            }
                        }
                    )
                }
            })
        }
    }

    /**
     * BUILD Pagination HTML List Items w/ Action Button and add to pagination <ol>
     * <li [classes]><button type=button [classes & attr]>a11y text</button></li>
     */
    public buildPaginationNavigationItems(): void {
        if (!this.paginationNavigation) {
            return
        }
        let navItems = ''
        // account for 0 index
        for (let i = 1; i <= this.totalPages; i++) {
            const currentClass =
                this.pageIndex === i - 1
                    ? carouselClass.paginationListItemActive
                    : ''

            navItems += `<li class="${carouselClass.paginationListItem}">
                <button type="button" class="${
                    carouselClass.paginationListItemButton
                } ${currentClass}"
                    ${carouselAttr.paginationNavToIndex}="${i - 1}">
                    <span class="${
                        CLASS_NAME.common.visuallyHidden
                    }">scroll to page ${i}</span>
                </button></li>`
        }
        // add to the pagination <ol>
        this.paginationNavigation.innerHTML = navItems
    }

    /**
     * Check that the pagination items were built/exist before removing hidden attribute
     * call fn to attach events to the buttons
     * Set flag that this has complete (at least once)
     */
    public showPaginationNavigation(): void {
        if (!this.paginationNavigation) {
            return
        }
        if (
            this.paginationNavigation.hasAttribute(HtmlAttr.HIDDEN) &&
            this.paginationNavigationActionItems.length > 0
        ) {
            this.paginationNavigation.removeAttribute(HtmlAttr.HIDDEN)
        }
        this.attachPaginationNavItemEvents()
        this.handlePaginationNavigationActiveView()
        this.paginationConfigured = true
    }

    /**
     * Run the configuration logic for Carousel Pagination depending on CarouselType
     * TYPE.SLIDE can easily build 1:1 list of navigation items, attach events
     * TYPE.LIST navigation items need to be built with params/responsive to screen
     */
    public configPaginationNavigationItems(): void {
        if (!this.paginationNavigation) {
            return
        }

        if (this.type == CarouselType.SLIDE && this.paginationConfigured) {
            // only TYPE.LIST would need to rebuild pagination
            // for responding to screen changes
            // Exit for type slide with configured flag true
            return
        }

        switch (this.type) {
            case CarouselType.LIST:
                this.buildPaginationNavigationItems()
                this.paginationNavigationActionItems = this.getElements(
                    `.${carouselClass.paginationListItemButton}`,
                    this.paginationNavigation
                )
                this.showPaginationNavigation()
                break
            case CarouselType.SLIDE:
            default:
                this.showPaginationNavigation()
                break
        }
    }

    /**
     * Place an active class on the current active pagination item
     * that matches the carousel's current page
     */
    public handlePaginationNavigationActiveView(): void {
        let currentActiveNavItemIndex = 0
        if (
            !this.paginationNavigation ||
            !this.paginationNavigationActionItems
        ) {
            return
        }
        // is it the first time?
        // initialize the current active item
        if (!this.paginationConfigured) {
            const currentPageNavItem = this.getElement(
                `[${carouselAttr.paginationNavToIndex}="${this.pageIndex}"]`,
                this.paginationNavigation
            )
            CSSUtils.addClass(
                currentPageNavItem,
                carouselClass.paginationListItemActive
            )
            return
        }
        // second time
        // What is the current active pagination item
        const currentActiveNavItem = this.getElement(
            `.${carouselClass.paginationListItemActive}`,
            this.paginationNavigation
        )
        if (currentActiveNavItem) {
            currentActiveNavItemIndex = this.getPaginationNavItemIndex(
                currentActiveNavItem
            )
        }
        // if the current active pagination item and the current page are the same
        // exit and make no changes
        if (
            currentActiveNavItemIndex == this.pageIndex &&
            currentActiveNavItemIndex < this.totalPages
        ) {
            return
        }
        // not the same -- find the pagination item that does match the current page
        // -1 for 0 index
        const currentPageNavItem = this.getElement(
            `[${carouselAttr.paginationNavToIndex}="${this.pageIndex}"]`,
            this.paginationNavigation
        )

        // swap the classes from the old active to new active item
        CSSUtils.removeClass(
            currentActiveNavItem,
            carouselClass.paginationListItemActive
        )
        CSSUtils.addClass(
            currentPageNavItem,
            carouselClass.paginationListItemActive
        )
    }

    /**
     * determine which direction buttons should be visible
     * apply tabindex -1 to hidden buttons for keyboard navigation
     */
    public handleButtonVisibility(): void {
        // Check previous button
        if (this.prevButton) {
            // is the scrolled amount less than threshold?
            // then there is no previous to go to
            if (
                this.lastScrollPosition <=
                CarouselComponent.buttonViewScrollThreshold
            ) {
                TabIndexUtils.hideTabIndex(this.prevButton)
                CSSUtils.removeClass(this.prevButton, carouselClass.btnVisible)
            } else {
                TabIndexUtils.removeTabIndex(this.prevButton)
                CSSUtils.addClass(this.prevButton, carouselClass.btnVisible)
            }
        }

        // Check next button
        if (this.nextButton && this.slider) {
            // is the scrolled amount more than or equal to the elements available scroll width?
            // then there is no next to go to
            if (
                this.pageIndex >= this.totalPages ||
                this.slider.scrollWidth -
                    this.slider.clientWidth -
                    this.lastScrollPosition <=
                    CarouselComponent.buttonViewScrollThreshold
            ) {
                TabIndexUtils.hideTabIndex(this.nextButton)
                CSSUtils.removeClass(this.nextButton, carouselClass.btnVisible)
            } else {
                TabIndexUtils.removeTabIndex(this.nextButton)
                CSSUtils.addClass(this.nextButton, carouselClass.btnVisible)
            }
        }
    }

    /**
     * scroll the previous slide or slides in view
     * scroll event will be triggered and reconsider view values
     */
    public goPrev(): void {
        if (!this.slider) {
            return
        }
        const prevIndex = this.pageIndex > 0 ? this.pageIndex - 1 : 0
        this.goToIndex(prevIndex)
    }

    /**
     * scroll the next slide or slides in views
     * scroll event will be triggered and reconsider view values
     */
    public goNext(): void {
        if (!this.slider) {
            return
        }
        const nextIndex =
            this.pageIndex < this.totalPages - 1
                ? this.pageIndex + 1
                : this.totalPages - 1
        this.goToIndex(nextIndex)
    }

    /**
     * scroll the given slide index
     * scroll event will be triggered and reconsider view values
     */
    public goToIndex(index: number): void {
        if (!this.slider) {
            return
        }
        this.slider.scrollLeft = this.indexScrollingDistance(index)
        this.pageIndex = index
        this.updatePageNumberUI()
        this.handleButtonVisibility()
    }
}
